import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close, EmojiEvents, Star, Remove, Search } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'

const currencyBRL = (n) =>
    (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const TYPE_LABELS = {
    invoice: 'Fatura',
    charge: 'Cobrança',
    transfer: 'Repasse',
    ted: 'TED',
    pix: 'PIX',
    payment_cashflow: 'Pagamento',
    payment: 'Pagamento',
}

const TYPE_COLORS = {
    invoice: '#3B82F6',
    charge: '#8B5CF6',
    transfer: '#F59E0B',
    ted: '#06B6D4',
    pix: '#22C55E',
    payment_cashflow: '#EF4444',
    payment: '#EF4444',
}

const FILTER_OPTIONS = [
    { key: '', label: 'Todos' },
    { key: 'invoice', label: 'Fatura' },
    { key: 'charge', label: 'Cobrança' },
    { key: 'transfer', label: 'Repasse' },
    { key: 'ted', label: 'TED' },
    { key: 'pix', label: 'PIX' },
    { key: 'payment_cashflow', label: 'Pagamento FC' },
    { key: 'payment', label: 'Pagamento' },
]

const getScoreConfig = (score) => {
    if (score >= 80)
        return { color: '#22C55E', bgColor: '#22C55E14', borderColor: '#22C55E30', icon: EmojiEvents, label: 'Alta' }
    if (score >= 50)
        return { color: '#F59E0B', bgColor: '#F59E0B14', borderColor: '#F59E0B30', icon: Star, label: 'Média' }
    return { color: '#94A3B8', bgColor: '#94A3B814', borderColor: '#94A3B830', icon: Remove, label: 'Baixa' }
}

const ReconciliationModal = ({ entry, onClose, onReconcile }) => {
    const { enqueueSnackbar } = useSnackbar()
    const [suggestions, setSuggestions] = useState(null)
    const [reconciling, setReconciling] = useState(false)
    const [manualMode, setManualMode] = useState(false)

    // Search state
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const [typeFilter, setTypeFilter] = useState('')
    const [searchText, setSearchText] = useState('')
    const [isSuggested, setIsSuggested] = useState(false)
    const debounceRef = useRef(null)

    useEffect(() => {
        if (entry) {
            setSuggestions(null)
            setManualMode(false)
            setSelectedItem(null)
            setSearchResults([])
            setSearchText('')
            setTypeFilter('')
            setIsSuggested(false)

            api.get(`cashflow/external-statement-entries/${entry.id}/suggestions/`)
                .then((res) => setSuggestions(res.data || []))
                .catch(() => {
                    setSuggestions([])
                    enqueueSnackbar('Erro ao buscar sugestões', { variant: 'error' })
                })
        }
    }, [entry])

    const doSearch = useCallback((searchVal, typeVal) => {
        const condoId = getCondoId()
        if (!condoId || !entry) return

        setSearchLoading(true)
        const params = { condo_id: condoId, limit: 20 }
        if (entry.entry_value) params.amount = Math.abs(entry.entry_value)
        if (searchVal) params.search = searchVal
        if (typeVal) params.type = typeVal

        api.get('cashflow/reconciliation-search/', { params })
            .then((res) => {
                const data = res.data || []
                setSearchResults(data)

                // Pre-select first result if it's the initial load (no user search)
                if (!searchVal && !typeVal && data.length > 0 && !selectedItem) {
                    setSelectedItem(data[0])
                    setIsSuggested(true)
                }
            })
            .catch(() => {
                setSearchResults([])
                enqueueSnackbar('Erro ao buscar registros', { variant: 'error' })
            })
            .finally(() => setSearchLoading(false))
    }, [entry, selectedItem])

    const handleManualModeOpen = () => {
        setManualMode(true)
        doSearch('', '')
    }

    const handleSearchChange = (value) => {
        setSearchText(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            doSearch(value, typeFilter)
        }, 500)
    }

    const handleTypeFilterChange = (newType) => {
        setTypeFilter(newType)
        doSearch(searchText, newType)
    }

    const handleSelectItem = (item) => {
        setSelectedItem(item)
        setIsSuggested(false)
    }

    const handleReconcileSuggestion = (suggestion) => {
        setReconciling(true)
        api.post(`cashflow/external-statement-entries/${entry.id}/reconcile/`, {
            target_type: suggestion.type,
            target_id: suggestion.id,
        })
            .then(() => {
                enqueueSnackbar('Lançamento conciliado com sucesso!', { variant: 'success' })
                onReconcile()
                onClose()
            })
            .catch(() => enqueueSnackbar('Erro ao conciliar', { variant: 'error' }))
            .finally(() => setReconciling(false))
    }

    const handleManualReconcile = () => {
        if (!selectedItem) {
            enqueueSnackbar('Selecione um registro para conciliar', { variant: 'warning' })
            return
        }

        setReconciling(true)
        api.post(`cashflow/external-statement-entries/${entry.id}/reconcile/`, {
            target_type: selectedItem.type,
            target_id: selectedItem.id,
        })
            .then(() => {
                enqueueSnackbar('Lançamento conciliado com sucesso!', { variant: 'success' })
                onReconcile()
                onClose()
            })
            .catch((err) => {
                const msg = err?.response?.data?.detail || 'Erro ao conciliar'
                enqueueSnackbar(msg, { variant: 'error' })
            })
            .finally(() => setReconciling(false))
    }

    if (!entry) return null

    return (
        <Dialog open={!!entry} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Conciliar Lançamento
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Entry details card */}
                <Card
                    sx={{
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Box>
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                {moment(entry.entry_date).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                {entry.description}
                            </Typography>
                            {entry.details && (
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                    {entry.details}
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: entry.entry_value >= 0 ? '#22C55E' : '#EF4444',
                            }}
                        >
                            {currencyBRL(entry.entry_value)}
                        </Typography>
                    </Box>
                    {(entry.document_number || entry.fitid) && (
                        <Box display="flex" gap={2} mt={1}>
                            {entry.document_number && (
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                    Doc: {entry.document_number}
                                </Typography>
                            )}
                            {entry.fitid && (
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                    FITID: {entry.fitid}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Card>

                {suggestions === null ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Automatic suggestions section */}
                        {suggestions.length > 0 && !manualMode && (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#94A3B8',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.05em',
                                        mb: 2,
                                        display: 'block',
                                    }}
                                >
                                    Sugestões de Conciliação
                                </Typography>

                                {suggestions.map((s, idx) => {
                                    const scoreConfig = getScoreConfig(s.score)
                                    const ScoreIcon = scoreConfig.icon
                                    return (
                                        <Card
                                            key={`${s.type}-${s.id}-${idx}`}
                                            sx={{
                                                mb: 1.5,
                                                p: 2,
                                                cursor: 'pointer',
                                                border: '1px solid #E2E8F0',
                                                borderRadius: 2,
                                                transition: 'all 0.15s',
                                                '&:hover': {
                                                    borderColor: '#3B82F6',
                                                    bgcolor: '#F8FAFC',
                                                },
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                        <ScoreIcon sx={{ fontSize: 18, color: scoreConfig.color }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 600, color: '#1E293B' }}
                                                        >
                                                            {s.description || s.number}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                                        {TYPE_LABELS[s.type] || s.type}
                                                        {s.number && ` - ${s.number}`}
                                                        {' • '}
                                                        {currencyBRL(s.amount)}
                                                        {s.date && ` • ${moment(s.date).format('DD/MM/YYYY')}`}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <Chip
                                                        label={`${s.score}%`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: scoreConfig.bgColor,
                                                            color: scoreConfig.color,
                                                            fontWeight: 700,
                                                            fontSize: '0.8rem',
                                                            border: `1px solid ${scoreConfig.borderColor}`,
                                                        }}
                                                    />
                                                    <LoadingButton
                                                        size="small"
                                                        variant="contained"
                                                        loading={reconciling}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleReconcileSuggestion(s)
                                                        }}
                                                        sx={{
                                                            bgcolor: '#3B82F6',
                                                            '&:hover': { bgcolor: '#2563EB' },
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            borderRadius: 1.5,
                                                            minWidth: 80,
                                                        }}
                                                    >
                                                        Conciliar
                                                    </LoadingButton>
                                                </Box>
                                            </Box>
                                        </Card>
                                    )
                                })}
                            </>
                        )}

                        {suggestions.length === 0 && !manualMode && (
                            <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                                Nenhuma sugestão automática encontrada. Use a busca manual abaixo.
                            </Typography>
                        )}

                        <Divider sx={{ my: 2.5 }} />

                        {!manualMode ? (
                            <Button
                                variant="text"
                                onClick={handleManualModeOpen}
                                sx={{ textTransform: 'none', color: '#64748B', fontWeight: 600 }}
                            >
                                Conciliar manualmente
                            </Button>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#94A3B8',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.05em',
                                        mb: 2,
                                        display: 'block',
                                    }}
                                >
                                    Conciliação Manual
                                </Typography>

                                {/* Type filter chips */}
                                <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
                                    {FILTER_OPTIONS.map((opt) => (
                                        <Chip
                                            key={opt.key}
                                            label={opt.label}
                                            size="small"
                                            onClick={() => handleTypeFilterChange(opt.key)}
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                bgcolor: typeFilter === opt.key
                                                    ? (TYPE_COLORS[opt.key] || '#3B82F6')
                                                    : '#F1F5F9',
                                                color: typeFilter === opt.key ? '#fff' : '#64748B',
                                                border: typeFilter === opt.key
                                                    ? 'none'
                                                    : '1px solid #E2E8F0',
                                                '&:hover': {
                                                    bgcolor: typeFilter === opt.key
                                                        ? (TYPE_COLORS[opt.key] || '#3B82F6')
                                                        : '#E2E8F0',
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>

                                {/* Search autocomplete */}
                                <Autocomplete
                                    options={searchResults}
                                    loading={searchLoading}
                                    value={selectedItem}
                                    onChange={(_, newValue) => handleSelectItem(newValue)}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input') handleSearchChange(value)
                                    }}
                                    getOptionLabel={(option) =>
                                        `${TYPE_LABELS[option.type] || option.type} - ${option.description} - ${currencyBRL(option.amount)}`
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.type === value.type && option.id === value.id
                                    }
                                    filterOptions={(x) => x}
                                    noOptionsText="Nenhum registro encontrado"
                                    loadingText="Buscando..."
                                    renderOption={(props, option) => {
                                        const color = TYPE_COLORS[option.type] || '#94A3B8'
                                        const isSelected = selectedItem?.type === option.type && selectedItem?.id === option.id
                                        const isFirstSuggested = isSuggested && searchResults[0]?.type === option.type && searchResults[0]?.id === option.id
                                        return (
                                            <Box
                                                component="li"
                                                {...props}
                                                key={`${option.type}-${option.id}`}
                                                sx={{
                                                    borderLeft: `3px solid ${color}`,
                                                    ml: 0.5,
                                                    '&.MuiAutocomplete-option': {
                                                        py: 1.5,
                                                        px: 2,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                                                        <Chip
                                                            label={TYPE_LABELS[option.type] || option.type}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: `${color}18`,
                                                                color: color,
                                                                fontWeight: 700,
                                                                fontSize: '0.65rem',
                                                                height: 20,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1E293B',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {option.description}
                                                        </Typography>
                                                        {isFirstSuggested && (
                                                            <Chip
                                                                label="Sugerido"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: '#22C55E18',
                                                                    color: '#22C55E',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.6rem',
                                                                    height: 18,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                                        {currencyBRL(option.amount)}
                                                        {option.date && ` • ${moment(option.date).format('DD/MM/YYYY')}`}
                                                        {option.extra_info && ` • ${option.extra_info}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Buscar por descrição, nome, número..."
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <Search sx={{ color: '#94A3B8', mr: 1, fontSize: 20 }} />
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />

                                {/* Selected item preview */}
                                {selectedItem && (
                                    <Card
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            border: `2px solid ${TYPE_COLORS[selectedItem.type] || '#3B82F6'}`,
                                            borderRadius: 2,
                                            bgcolor: `${TYPE_COLORS[selectedItem.type] || '#3B82F6'}08`,
                                            boxShadow: 'none',
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                    <Chip
                                                        label={TYPE_LABELS[selectedItem.type] || selectedItem.type}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: TYPE_COLORS[selectedItem.type] || '#3B82F6',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '0.7rem',
                                                        }}
                                                    />
                                                    {isSuggested && (
                                                        <Chip
                                                            label="Sugestão automática"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#22C55E18',
                                                                color: '#22C55E',
                                                                fontWeight: 600,
                                                                fontSize: '0.65rem',
                                                                height: 20,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                    {selectedItem.description}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#64748B' }}>
                                                    {currencyBRL(selectedItem.amount)}
                                                    {selectedItem.date && ` • ${moment(selectedItem.date).format('DD/MM/YYYY')}`}
                                                    {selectedItem.extra_info && ` • ${selectedItem.extra_info}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>
                    Cancelar
                </Button>
                {manualMode && (
                    <LoadingButton
                        variant="contained"
                        loading={reconciling}
                        disabled={!selectedItem}
                        onClick={handleManualReconcile}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                        }}
                    >
                        Conciliar
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ReconciliationModal
